type WebContentItem =
  | string
  | {
      url?: unknown
      link?: unknown
      title?: unknown
      snippet?: unknown
      content?: unknown
      contents?: unknown
    }

type LibraryContentItem = {
  library?: unknown
  sourcefile?: unknown
  sourcepage?: unknown
  title?: unknown
  snippet?: unknown
  content?: unknown
  contents?: unknown
}

type M365ContentItem = {
  id?: unknown
  type?: unknown
  title?: unknown
  subject?: unknown
  snippet?: unknown
  content?: unknown
  contents?: unknown
  url?: unknown
  extra_data?: unknown
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

function getStringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : ""
}

function getRecordValue(record: unknown, key: string): unknown {
  if (typeof record !== "object" || record === null) return undefined
  return (record as Record<string, unknown>)[key]
}

function getItemStringValue(item: M365ContentItem, key: string): string {
  return (
    getStringValue(getRecordValue(item, key)) ||
    getStringValue(getRecordValue(item.extra_data, key))
  )
}

function getWebContentItems(supportingContent: unknown): WebContentItem[] {
  if (typeof supportingContent !== "object" || supportingContent === null) return []

  const webContent = (supportingContent as { web_content?: unknown }).web_content
  if (!Array.isArray(webContent)) return []

  return webContent.filter(
    (item): item is WebContentItem =>
      typeof item === "string" || (typeof item === "object" && item !== null)
  )
}

function getM365ContentItems(supportingContent: unknown): M365ContentItem[] {
  if (typeof supportingContent !== "object" || supportingContent === null) return []

  const m365Content = (supportingContent as { m365_content?: unknown }).m365_content
  if (!Array.isArray(m365Content)) return []

  return m365Content.filter(
    (item): item is M365ContentItem => typeof item === "object" && item !== null
  )
}

function getLibraryContentItems(supportingContent: unknown): LibraryContentItem[] {
  if (typeof supportingContent !== "object" || supportingContent === null) return []

  const libraryContent = (supportingContent as { library_content?: unknown }).library_content
  if (!Array.isArray(libraryContent)) return []

  return libraryContent.filter(
    (item): item is LibraryContentItem => typeof item === "object" && item !== null
  )
}

function webContentItemToXml(item: WebContentItem): string | null {
  if (typeof item === "string") {
    const trimmed = item.trim()
    if (!trimmed) return null
    if (trimmed.startsWith("<")) return trimmed

    const match = trimmed.match(/^(https?:\/\/\S+):\s+([\s\S]+)$/i)
    if (!match) return null

    return `<web_result url="${escapeXml(match[1])}"><content>${escapeXml(match[2])}</content></web_result>`
  }

  const url = getStringValue(item.url) || getStringValue(item.link)
  const title = getStringValue(item.title)
  const content =
    getStringValue(item.snippet) || getStringValue(item.content) || getStringValue(item.contents)

  if (!url && !content) return null

  const titleXml = title ? `<title>${escapeXml(title)}</title>` : ""
  return `<web_result url="${escapeXml(url)}">${titleXml}<content>${escapeXml(content)}</content></web_result>`
}

function libraryContentItemToXml(item: LibraryContentItem): string | null {
  const library = getStringValue(item.library)
  const filename =
    getStringValue(item.sourcepage) || getStringValue(item.sourcefile) || getStringValue(item.title)
  const content =
    getStringValue(item.snippet) || getStringValue(item.content) || getStringValue(item.contents)

  if (!library || !filename || !content) return null

  return `<document id="${escapeXml(library)}:${escapeXml(filename)}"><metadata><library>${escapeXml(library)}</library><filename>${escapeXml(filename)}</filename></metadata><content>${escapeXml(content)}</content></document>`
}

function optionalXmlTag(name: string, value: string): string {
  return value ? `<${name}>${escapeXml(value)}</${name}>` : ""
}

function m365ContentItemToXml(item: M365ContentItem): string | null {
  const id = getStringValue(item.id)
  if (!id) return null

  const type = getStringValue(item.type)
  const title = getStringValue(item.title) || getItemStringValue(item, "subject") || id
  const content =
    getStringValue(item.snippet) || getStringValue(item.content) || getStringValue(item.contents)
  const url = getItemStringValue(item, "url")
  const sender = getItemStringValue(item, "sender")
  const date = getItemStringValue(item, "date")

  return `<document id="${escapeXml(id)}"><metadata><source>microsoft_365</source>${optionalXmlTag("type", type)}${optionalXmlTag("title", title)}${optionalXmlTag("url", url)}${optionalXmlTag("sender", sender)}${optionalXmlTag("date", date)}</metadata><content>${escapeXml(content)}</content></document>`
}

export function getStructuredSupportingContentItems(supportingContent: unknown): string[] {
  const webItems = getWebContentItems(supportingContent)
    .map((item) => webContentItemToXml(item))
    .filter((item): item is string => Boolean(item))
  const libraryItems = getLibraryContentItems(supportingContent)
    .map((item) => libraryContentItemToXml(item))
    .filter((item): item is string => Boolean(item))
  const m365Items = getM365ContentItems(supportingContent)
    .map((item) => m365ContentItemToXml(item))
    .filter((item): item is string => Boolean(item))

  return [...webItems, ...libraryItems, ...m365Items]
}
